import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import GlobalNavbar from './_components/GlobalNavbar';
import GlobalSidebar from './_components/GlobalSidebar';

import { NAV_HEIGHT, SIDEBAR_WIDTH } from '@/shared/constants';

import { useMainLayoutLogic } from './_useMainLayoutLogic';
import { useIsMobileShell } from './_useIsMobileShell';
import { MOBILE_BREAKPOINT_PX, MOBILE_SHELL_MAX_WIDTH_PX } from './_constants';

import AnimatedOutlet from './_components/AnimatedOutlet';
import AppTopBar from './_components/AppTopBar';
import BottomNavBar from './_components/BottomNavBar';
import MobileGlobalSidebar from './_components/MobileGlobalSidebar';
import { useBottomNavStore } from './_useBottomNavStore';
import { useTopBarHandle } from './_useTopBarHandle';

interface MainLayoutProps {
  hideNavbar?: boolean;
  hideSidebar?: boolean;
}

/**
 * 대부분의 페이지에서 사용할 레이아웃 컴포넌트.
 * 네이티브 앱이거나(student/parent 전용 빌드), 웹에서 student/parent가 모바일 폭으로 접속하면
 * 앱 셸(useIsMobileShell 참고)을 보여준다. admin/staff/instructor는 화면 폭과 무관하게 항상
 * 데스크톱 셸을 쓴다.
 */
export default function MainLayout(props: MainLayoutProps) {
  const isMobileShell = useIsMobileShell();

  if (isMobileShell) {
    return <MobileMainLayout hideTopBar={props.hideNavbar} />;
  }
  return <DesktopMainLayout {...props} />;
}

/** 기존 데스크톱 대시보드 셸 (GNB + LNB 고정 그리드) — 변경 없음 */
function DesktopMainLayout({ hideNavbar, hideSidebar }: MainLayoutProps) {
  const { sidebarMenu } = useMainLayoutLogic();

  const style = {
    '--nav-h': hideNavbar ? '0px' : NAV_HEIGHT,
    '--sidebar-w': hideSidebar ? '0px' : SIDEBAR_WIDTH.COLLAPSED,
  };

  return (
    <S.Layout style={style as React.CSSProperties}>
      {/* GNB */}
      {!hideNavbar && <GlobalNavbar />}

      {/* LNB */}
      {!hideSidebar && <GlobalSidebar menu={sidebarMenu} />}

      {/* Main Content */}
      <S.Main className='custom_scroll'>
        <Outlet />
      </S.Main>
    </S.Layout>
  );
}

/**
 * 모바일(Capacitor) 셸 — 상단바 + 본문(스크롤) + 하단 탭바 3행 그리드, 사이드바는 그리드 밖 fixed 드로어.
 * capacitor-gate-erp의 MainLayout을 그대로 이식 (AnimatedOutlet/AppTopBar/BottomNavBar/MobileGlobalSidebar).
 */
function MobileMainLayout({ hideTopBar }: { hideTopBar?: boolean }) {
  const { mobileSidebarMenu, bottomNavItems } = useMainLayoutLogic();
  const isBottomNavHidden = useBottomNavStore((s) => s.isHidden);
  const visibleBottomNavItems = isBottomNavHidden ? [] : bottomNavItems;
  const hasBottomNav = visibleBottomNavItems.length > 0;

  // AppTopBar와 같은 useTopBarHandle()을 써서 title 행 유무를 같은 렌더 틱에 동일하게 판단한다.
  const { hasRow } = useTopBarHandle();
  const navH = hideTopBar ? '0px' : hasRow ? '5.75rem' : '3.5rem';
  const footerH = hasBottomNav ? '3.55rem' : '0px';

  const mobileStyle = {
    '--nav-h': navH,
    '--header-h': `calc(${navH} + env(safe-area-inset-top))`,
    '--footer-h': `calc(${footerH} + env(safe-area-inset-bottom))`,
  } as React.CSSProperties;

  return (
    <S.MobileLayout style={mobileStyle}>
      {!hideTopBar && <AppTopBar />}

      <S.MobileMain>
        <AnimatedOutlet hasBottomNav={hasBottomNav} />
      </S.MobileMain>

      <BottomNavBar items={visibleBottomNavItems} />

      {/* LNB — fixed 드로어 */}
      <MobileGlobalSidebar menu={mobileSidebarMenu} />
    </S.MobileLayout>
  );
}

const S = {
  Layout: styled.div`
    position: fixed; /* 전체 고정 */
    inset: 0; /* top:0 right:0 bottom:0 left:0 */
    display: grid;
    grid-template-rows: var(--nav-h) 1fr; /* 헤더 + 본문 */
    grid-template-columns: var(--sidebar-w) minmax(0, 1fr);
    overflow: hidden; /* 페이지 스크롤 차단 */

    @media (max-width: ${MOBILE_BREAKPOINT_PX}px) {
      grid-template-columns: 0 minmax(0, 1fr); /* 모바일: 사이드바 열 제거 */
    }
  `,
  Main: styled.main`
    position: relative;
    grid-row: 2;
    grid-column: 2;
    min-width: 0;
  `,
  MobileLayout: styled.div`
    position: fixed;
    inset: 0;
    /* PC 웹: 폰 폭 컬럼을 가운데 정렬. transform으로 fixed 자손(드로어/딤)이 이 컬럼 기준으로 배치된다 */
    max-width: ${MOBILE_SHELL_MAX_WIDTH_PX}px;
    margin: 0 auto;
    transform: translateZ(0);
    box-shadow: 0 0 0 1px rgb(0 0 0 / 0.06);
    display: grid;
    grid-template-rows: auto 1fr auto;
    overflow: hidden;
  `,
  MobileMain: styled.main`
    grid-row: 2;
    position: relative;
    min-width: 0;
    overflow: hidden;
  `,
};
